import { useRecordBankReturn } from "Api/Mutations/Transactions/useRecordBankReturn";
import { hasPaymentFlowMarker } from "Utils/PaymentFlowUtils";
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from "Utils/Storage/SessionStorage/SecureSessionStorage";
import { getBankReturnRelayedKey } from "Utils/Storage/SessionStorage/SessionStorageKeys";
import { useEffect } from "react";

type Props = {
  publicID: string;
};

/**
 * Once per landing: when the customer arrives from the bank (paymentFlow marker in the URL), send the
 * landing URL's query string to the backend so a rejection reported only in the redirect closes the payment.
 * The raw `window.location.search` is relayed instead of parsed values — the backend knows each bank's format.
 *
 * The landing URL with the bank's parameters stays in the browser history, so a refresh, a remount or the Back
 * button would relay the same query again — during a retry that would fail the fresh attempt. What was relayed
 * is remembered for this tab; when storage is unavailable the backend's own guards still apply.
 */
export const useBankReturnRelay = ({ publicID }: Props) => {
  const { mutate } = useRecordBankReturn(publicID);

  useEffect(() => {
    const search = window.location.search;
    if (!hasPaymentFlowMarker(search)) {
      return;
    }

    const relayedKey = getBankReturnRelayedKey(publicID, search);
    if (getSessionStorageItem(relayedKey) !== null) {
      return;
    }

    setSessionStorageItem(relayedKey, "true");
    mutate(search);
  }, [publicID, mutate]);
};
